import React, { useState } from 'react';
import { Button, Input, InputNumber, Select, Switch } from 'antd';

import DefaultValueField from '../cards/DefaultValueField';
import TextOptions from '../cards/TextOptions';
import DataTypePicker from './DataTypePicker';
import ToggleRow from './ToggleRow';
import { ACCEPT_OPTIONS } from '../cards/LocationOptions';
import { optionSearchFilter } from '../Input';
import { TRASH } from '../icons';
import { ELEMENT_OPTION_TOGGLES, INPUT_OPTION_KEYS_EXCLUDED, fractionsAvailable, getOptionKeysForInput, isIdentifierNumber, percentAvailable, pruneOptionsForType, showsAsAmount, sliderAvailable, toggleDescription } from '../../config/elementOptionPolicy';
import { defaultForMultiToggle } from '../../utils/choiceValues';
import { RATING_SCALE_CHOICES, ratingScale } from '../../utils/rating';
import { NUMBER_LIMITS } from '../../utils/elementValidation';
import { countryOptions } from '../../utils/countries';
import { DEFAULT_COUNTRY, normalizeCountry } from '../../utils/elementFormat/phone';
import { getMeasure, isUnitOf } from '../../utils/units';
import { applyMeasurePicker, measurePickerOptions, measurePickerValue, perOptionsFor, perWord, unitOptionsFor } from '../../utils/measurements';
import { allowedCodes, defaultCodeOf } from '../../utils/currency';
import { allCurrencyOptionGroups, currencyOption, currencySearchFilter } from '../../utils/money';
import { normalizeId } from '../../utils/ids';

/**
 * One ad hoc input, as a person authors it: a head row (label, type,
 * required, remove) and, under it, the element-grade options its type takes
 * (help text, choices, lookup card, measure and units, decimals, bounds, the
 * yes/no labels, the default value, the toggle rows).
 *
 * It is the multi add form's input editor, lifted out so a card action's
 * press-time inputs are the same thing (keepr-api validates both with
 * utils/multiAddForms/validate.js validateInputs, KPR-382). The host owns the
 * list; this owns one input and hands back the WHOLE next input:
 *
 *   input     { uid, key, label, dataType, options }
 *   onChange  (nextInput, { typeChanged }) => void. A type change has already
 *             pruned the options the new type does not take; a host that keeps
 *             references to the input (a multi add form's mappings) drops them
 *             when `typeChanged` is true.
 *   onRemove  () => void
 *   cardOptions   the cards a lookup input may point at (Select options)
 *   moneyCode     the currency a new money input starts in
 *   excludedTypes the types the host's API refuses as an input
 *   dead      the input is flagged (a multi add input nothing receives)
 *   testId    data-testid of the input's box
 *
 * The class names keep the multi add editor's (css/components/multi-add.css):
 * one look for both screens.
 */

/**
 * The types an ad hoc input never takes (PR 10b: a file needs an upload;
 * KPR-390: a "Me too" count is kept by keepr from the people who confirm;
 * KPR-679: a repeating group is a list of entries no input asks for yet).
 * keepr-api utils/multiAddForms/validate.js NEVER_INPUT_TYPES.
 */
export const AD_HOC_INPUT_EXCLUDED_TYPES = ['file', 'confirmations', 'repeating-group'];

// A rate is picked as the measure it counts plus a Per, as on a card element.
const MEASURE_OPTIONS = measurePickerOptions();
// The input options that name a unit or a bound in one: a new measure clears them.
const INPUT_MEASURE_SCOPED_KEYS = ['units', 'min', 'max'];

// The input types whose default is a bare number (the policy's other
// default types — choice, yes/no, text — take the DefaultValueField).
const NUMERIC_INPUT_TYPES = ['number', 'decimal', 'integer'];

const finiteOrUndefined = v => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const intOrUndefined = v => (typeof v === 'number' && Number.isInteger(v) ? v : undefined);

/** Inputs whose choices are typed one per line. */
export const choicesText = options => (Array.isArray(options?.choices) ? options.choices : []).map(c => (c && typeof c === 'object' ? c.label || c.value : c)).join('\n');
export const parseChoices = text => String(text || '').split('\n').map(line => line.trim()).filter(Boolean).map(line => ({ value: line, label: line }));

/**
 * What stops an input from being saved, in a sentence, or null: the answers
 * the API's typed gate would refuse with nothing to say them against.
 */
export const adHocInputProblem = input => {
	if (!String(input?.label || '').trim()) return 'Every input needs a label.';
	if (input.dataType === 'choice' && !(input.options?.choices || []).length) return `"${input.label}" needs at least one choice.`;
	if (input.dataType === 'card-lookup' && !input.options?.lookupCardId) return `"${input.label}" needs a lookup card.`;
	if (input.dataType === 'measurement' && !input.options?.measure) return `"${input.label}" needs a measure.`;
	if (input.dataType === 'currency' && !allowedCodes(input.options).length) return `"${input.label}" needs at least one currency.`;
	return null;
};

export default function AdHocInputEditor({
	input,
	index,
	onChange,
	onRemove,
	cardOptions = [],
	moneyCode = 'USD',
	excludedTypes = AD_HOC_INPUT_EXCLUDED_TYPES,
	dead = false,
	testId,
}) {
	// What the author has typed into a phone input's country search box — the
	// box is how a region outside the short list is reached (utils/countries),
	// and it is UI state, never part of the input.
	const [countrySearch, setCountrySearch] = useState('');

	const patch = partial => onChange({ ...input, ...partial });
	// An undefined value in the patch REMOVES the key, so a cleared option
	// leaves the wire body rather than riding along as null.
	const patchOptions = partial => {
		const options = { ...(input.options || {}), ...partial };
		Object.keys(options).forEach(key => { if (options[key] === undefined) delete options[key]; });
		onChange({ ...input, options });
	};
	const setType = dataType => {
		// A type change drops every option the new type does not offer — the
		// card editor's rule, shared (config/elementOptionPolicy.js
		// pruneOptionsForType), so a type change means the same thing in both
		// editors. The keys an INPUT may not carry at all go with it.
		const kept = pruneOptionsForType(input.options, dataType, input.dataType);
		INPUT_OPTION_KEYS_EXCLUDED.forEach(key => { delete kept[key]; });
		// The shared rule keeps a choice list across types for a card element,
		// whose stored values a change back to Choice can reuse (KPE-033). An
		// input stores no values, so a list off a Choice input is only a list
		// nobody can see: it goes.
		if (dataType !== 'choice') delete kept.choices;
		// A money input is never without a currency (the API's typed gate
		// requires one), so a new one starts in the collection's, else the
		// author's own, else US dollars.
		if (dataType === 'currency' && !(Array.isArray(kept.currencies) && kept.currencies.length)) kept.currencies = [moneyCode];
		onChange({ ...input, dataType, options: kept }, { typeChanged: dataType !== input.dataType });
	};

	// Measurement options keep the card editor's invariants (MeasurementOptions):
	// a new measure — or a rate turned on or off — resets every unit-bearing
	// option, another time moves them to it (utils/measurements
	// applyMeasurePicker); the allowlist, when there is one, always holds the
	// default unit (the API refuses one without).
	const setMeasurePicker = (measureId, per) => {
		const next = applyMeasurePicker(input.options || {}, measureId, per, INPUT_MEASURE_SCOPED_KEYS);
		patchOptions({ measure: undefined, defaultUnit: undefined, units: undefined, min: undefined, max: undefined, ...next });
	};
	const setMeasure = measureId => setMeasurePicker(measureId, measurePickerValue(input.options).per);
	const setPer = per => setMeasurePicker(measurePickerValue(input.options).measure, per);
	const setDefaultUnit = unitId => {
		const options = input.options || {};
		const allowlist = Array.isArray(options.units) ? options.units : [];
		patchOptions({ defaultUnit: unitId, ...(allowlist.length && !allowlist.includes(unitId) ? { units: [...allowlist, unitId] } : {}) });
	};
	const setUnits = ids => {
		const options = input.options || {};
		const list = (ids || []).filter(id => isUnitOf(id, options.measure));
		if (list.length && options.defaultUnit && !list.includes(options.defaultUnit)) list.push(options.defaultUnit);
		patchOptions({ units: list.length ? list : undefined });
	};
	// Currency options keep the card editor's invariants (CurrencyOptions): a
	// default that falls out of the list is dropped, never left dangling.
	const setCurrencies = codes => {
		const options = input.options || {};
		const list = (codes || []).filter((code, i, all) => all.indexOf(code) === i);
		patchOptions({ currencies: list, ...(options.defaultCurrency && !list.includes(options.defaultCurrency) ? { defaultCurrency: undefined } : {}) });
	};

	// An ad hoc input's options past the head row: the element-grade set the
	// policy offers for its type, laid out like the card element editor but
	// compact — labelled fields on a two-column grid, then the boolean
	// options as a divided run of toggle rows.
	const renderOptions = () => {
		const keys = getOptionKeysForInput(input.dataType);
		const options = input.options || {};
		const n = index + 1;
		const idFor = key => `multi-add-input-${input.uid}-${key}`;
		const isMeasurementInput = keys.includes('measure');
		const measure = isMeasurementInput ? getMeasure(options.measure) : null;
		const picked = measurePickerValue(options);
		const perOptions = isMeasurementInput ? perOptionsFor(picked.measure) : [];
		const per = measure?.rateOf ? picked.per : null;
		const unitOptions = measure ? unitOptionsFor(measure.id, per) : [];
		const allUnitOptions = measure && per ? unitOptionsFor(measure.id) : unitOptions;
		const allowlist = measure && Array.isArray(options.units) ? options.units.filter(id => isUnitOf(id, measure.id)) : [];
		// PR 7: an identifier is never a percentage and never grouped (the API
		// refuses the pair), so Leading zeros and the two switches withhold
		// each other — each kept while set, so a stored pair can be cleared.
		const amountWithheld = key => {
			if (options[key] === true) return false;
			if (key === 'thousands') return isIdentifierNumber(options);
			// KPR-1025: a percent and a fraction withhold each other too.
			if (key === 'percent') return !percentAvailable(options);
			if (key === 'fractions') return !fractionsAvailable(options);
			return false;
		};
		const toggles = ELEMENT_OPTION_TOGGLES.filter(row => row.key !== 'required' && keys.includes(row.key) && !amountWithheld(row.key));
		const showLeadingZeros = !showsAsAmount(options) || intOrUndefined(options.leadingZeros) !== undefined;
		const field = (key, label, control, full = false) => (
			<div className={`multi-add-editor__input-field${full ? ' multi-add-editor__input-field--full' : ''}`} key={key}>
				<label htmlFor={idFor(key)}>{label}</label>
				{control}
			</div>
		);
		return (
			<>
				<div className="multi-add-editor__input-grid">
					{/* Every type: the words the person adding gets on the field's
					    label (FieldHelp), same as a card element's help text. */}
					{field('help', 'Help text', (
						<Input.TextArea
							id={idFor('help')}
							size="small"
							autoSize={{ minRows: 1, maxRows: 4 }}
							placeholder="Optional — what to enter, how to measure it, where to find it"
							aria-label={`Input ${n} help text`}
							value={options.help || ''}
							onChange={event => patchOptions({ help: event.target.value || undefined })}
						/>
					), true)}
					{keys.includes('choices') ? field('choices', 'Choices', (
						<Input.TextArea
							id={idFor('choices')}
							size="small"
							rows={3}
							placeholder="One per line"
							aria-label={`Input ${n} choices`}
							value={choicesText(options)}
							onChange={event => patchOptions({ choices: parseChoices(event.target.value) })}
						/>
					), true) : null}
					{keys.includes('lookupCardId') ? field('lookupCardId', 'Lookup card', (
						<Select
							id={idFor('lookupCardId')}
							size="small"
							placeholder="Which card?"
							aria-label={`Input ${n} lookup card`}
							value={options.lookupCardId || undefined}
							onChange={cardId => patchOptions({ lookupCardId: normalizeId(cardId) })}
							options={cardOptions}
							showSearch
							filterOption={optionSearchFilter}
							popupClassName="card-picker-dropdown"
						/>
					), true) : null}
					{keys.includes('measure') ? field('measure', 'Measure', (
						<Select
							id={idFor('measure')}
							size="small"
							placeholder="What is measured"
							aria-label={`Input ${n} measure`}
							value={picked.measure || undefined}
							onChange={measureId => setMeasure(measureId)}
							options={MEASURE_OPTIONS}
							showSearch
							filterOption={optionSearchFilter}
						/>
					)) : null}
					{perOptions.length ? field('per', 'Per', (
						<Select
							id={idFor('per')}
							size="small"
							aria-label={`Input ${n} per`}
							value={picked.per}
							onChange={nextPer => setPer(nextPer)}
							options={perOptions}
						/>
					)) : null}
					{keys.includes('defaultUnit') ? field('defaultUnit', 'Default unit', (
						<Select
							id={idFor('defaultUnit')}
							size="small"
							placeholder="Default unit"
							aria-label={`Input ${n} default unit`}
							value={options.defaultUnit || undefined}
							onChange={unitId => setDefaultUnit(unitId)}
							options={unitOptions}
							disabled={!measure}
							showSearch
							filterOption={optionSearchFilter}
						/>
					)) : null}
					{keys.includes('units') ? field('units', 'Allowed units', (
						<Select
							id={idFor('units')}
							size="small"
							mode="multiple"
							placeholder={per ? `Every unit ${perWord(per)}` : 'Every unit of the measure'}
							aria-label={`Input ${n} allowed units`}
							value={allowlist}
							onChange={ids => setUnits(ids)}
							options={allUnitOptions}
							disabled={!measure}
							filterOption={optionSearchFilter}
						/>
					), true) : null}
					{/* A money input's currencies and its default — the card
					    element editor's pair (components/cards/CurrencyOptions.js). */}
					{keys.includes('currencies') ? field('currencies', 'Currencies', (
						<Select
							id={idFor('currencies')}
							size="small"
							mode="multiple"
							placeholder="Search currencies"
							aria-label={`Input ${n} currencies`}
							value={allowedCodes(options)}
							onChange={codes => setCurrencies(codes)}
							options={allCurrencyOptionGroups()}
							filterOption={currencySearchFilter}
						/>
					), true) : null}
					{keys.includes('defaultCurrency') && allowedCodes(options).length > 1 ? field('defaultCurrency', 'Default currency', (
						<Select
							id={idFor('defaultCurrency')}
							size="small"
							aria-label={`Input ${n} default currency`}
							value={defaultCodeOf(options) || undefined}
							onChange={code => patchOptions({ defaultCurrency: code || undefined })}
							options={allowedCodes(options).map(currencyOption).filter(Boolean)}
						/>
					)) : null}
					{/* A phone input's country and a location input's accepted
					    halves — the same two the card element editors offer
					    (components/cards/PhoneOptions.js, LocationOptions.js),
					    so an ad hoc input reads and refuses the way a stored
					    element of that type would. */}
					{keys.includes('country') ? field('country', 'Country', (
						<Select
							id={idFor('country')}
							size="small"
							aria-label={`Input ${n} country`}
							value={normalizeCountry(options.country) || DEFAULT_COUNTRY}
							onChange={code => patchOptions({ country: code })}
							onSearch={next => setCountrySearch(next)}
							options={countryOptions({ value: options.country, search: countrySearch })}
							showSearch
							filterOption={optionSearchFilter}
						/>
					)) : null}
					{keys.includes('accept') ? field('accept', 'Accepts', (
						<Select
							id={idFor('accept')}
							size="small"
							aria-label={`Input ${n} accepts`}
							value={ACCEPT_OPTIONS.some(o => o.value === options.accept) ? options.accept : 'both'}
							onChange={next => patchOptions({ accept: next === 'both' ? undefined : next })}
							options={ACCEPT_OPTIONS}
						/>
					)) : null}
					{/* A number input's decimals and leading zeros — the same two
					    the card element editor offers (components/cards/
					    NumberOptions.js), so an ad hoc input reads and rounds
					    the way a stored element of that type would. */}
					{keys.includes('decimals') ? field('decimals', 'Decimals', (
						<InputNumber
							id={idFor('decimals')}
							size="small"
							aria-label={`Input ${n} decimals`}
							min={0}
							max={NUMBER_LIMITS.MAX_DECIMALS}
							step={1}
							precision={0}
							disabled={input.dataType === 'integer'}
							value={input.dataType === 'integer' ? 0 : (intOrUndefined(options.decimals) ?? null)}
							onChange={v => patchOptions({ decimals: intOrUndefined(v) })}
							placeholder="Any"
							inputMode="numeric"
						/>
					)) : null}
					{keys.includes('leadingZeros') && showLeadingZeros ? field('leadingZeros', 'Leading zeros', (
						<InputNumber
							id={idFor('leadingZeros')}
							size="small"
							aria-label={`Input ${n} leading zeros`}
							min={NUMBER_LIMITS.MIN_LEADING_ZEROS}
							max={NUMBER_LIMITS.MAX_LEADING_ZEROS}
							step={1}
							precision={0}
							value={intOrUndefined(options.leadingZeros) ?? null}
							onChange={v => patchOptions({ leadingZeros: intOrUndefined(v) })}
							placeholder="Off"
							inputMode="numeric"
						/>
					)) : null}
					{/* A yes/no input's two states in the author's own words. */}
					{keys.includes('trueLabel') ? field('trueLabel', 'Label for yes', (
						<Input
							id={idFor('trueLabel')}
							size="small"
							placeholder="Yes"
							aria-label={`Input ${n} label for yes`}
							value={options.trueLabel || ''}
							onChange={event => patchOptions({ trueLabel: event.target.value || undefined })}
						/>
					)) : null}
					{keys.includes('falseLabel') ? field('falseLabel', 'Label for no', (
						<Input
							id={idFor('falseLabel')}
							size="small"
							placeholder="No"
							aria-label={`Input ${n} label for no`}
							value={options.falseLabel || ''}
							onChange={event => patchOptions({ falseLabel: event.target.value || undefined })}
						/>
					)) : null}
					{/* min / max belong to both the numeric types and
					    measurement; only a measurement needs its measure
					    picked before a bound means anything. */}
					{keys.includes('min') ? field('min', 'Minimum', (
						<InputNumber
							id={idFor('min')}
							size="small"
							aria-label={`Input ${n} minimum`}
							value={finiteOrUndefined(options.min) ?? null}
							onChange={v => patchOptions({ min: finiteOrUndefined(v) })}
							disabled={isMeasurementInput && !measure}
							controls={false}
							inputMode="decimal"
						/>
					)) : null}
					{/* A rating's `max` is its scale — how many stars — not a
					    bound, so it is picked from the 2–10 the API accepts. */}
					{keys.includes('max') && input.dataType === 'rating' ? field('max', 'Stars', (
						<Select
							id={idFor('max')}
							size="small"
							aria-label={`Input ${n} stars`}
							value={ratingScale(options).max}
							options={RATING_SCALE_CHOICES.map(v => ({ value: v, label: `${v} stars` }))}
							onChange={v => patchOptions({ max: v })}
						/>
					)) : null}
					{keys.includes('max') && input.dataType !== 'rating' ? field('max', 'Maximum', (
						<InputNumber
							id={idFor('max')}
							size="small"
							aria-label={`Input ${n} maximum`}
							value={finiteOrUndefined(options.max) ?? null}
							onChange={v => patchOptions({ max: finiteOrUndefined(v) })}
							disabled={isMeasurementInput && !measure}
							controls={false}
							inputMode="decimal"
						/>
					)) : null}
					{/* PR 7: what the input's − / + and slider move by, and the
					    slider itself (only between a minimum below a maximum). */}
					{keys.includes('step') ? field('step', 'Step', (
						<InputNumber
							id={idFor('step')}
							size="small"
							aria-label={`Input ${n} step`}
							min={0}
							value={finiteOrUndefined(options.step) ?? null}
							onChange={v => patchOptions({ step: finiteOrUndefined(v) && v > 0 ? v : undefined })}
							placeholder="1"
							controls={false}
							inputMode="decimal"
						/>
					)) : null}
					{keys.includes('control') ? field('control', 'Show as', (
						<Select
							id={idFor('control')}
							size="small"
							aria-label={`Input ${n} show as`}
							value={options.control === 'slider' ? 'slider' : 'input'}
							onChange={v => patchOptions({ control: v === 'slider' ? 'slider' : undefined })}
							options={[
								{ value: 'input', label: 'Number' },
								{ value: 'slider', label: 'Slider (needs a minimum and a maximum)', disabled: !sliderAvailable(options) && options.control !== 'slider' },
							]}
						/>
					)) : null}
					{/* A number input's default is the bare number; a choice, a
					    yes/no and a text take theirs in the input's own control
					    below (DefaultValueField, PR 4). */}
					{keys.includes('defaultValue') && NUMERIC_INPUT_TYPES.includes(input.dataType) ? field('defaultValue', 'Default value', (
						<InputNumber
							id={idFor('defaultValue')}
							size="small"
							aria-label={`Input ${n} default value`}
							value={finiteOrUndefined(options.defaultValue) ?? null}
							onChange={v => patchOptions({ defaultValue: finiteOrUndefined(v) })}
							controls={false}
							inputMode="decimal"
						/>
					)) : null}
				</div>
				{/* A text input's rules — the card element's Text panel, compact:
				    its own maximum length, and a short text's case and pattern
				    (the API validates them with the same gate, the pattern's
				    safety check included). "Trim spaces" is a toggle below. */}
				{keys.includes('maxLength') ? (
					<TextOptions
						compact
						idPrefix={`multi-add-input-${input.uid}`}
						dataType={input.dataType}
						options={options}
						onPatch={partial => patchOptions(partial)}
					/>
				) : null}
				{/* A choice, yes/no or text input's default (PR 4,
				    docs/plans/data-types-second-pass.md §3.6): typed into the
				    input's own control, held to its own rules, seeded on the
				    form when it opens — the submit stores what was sent. */}
				{keys.includes('defaultValue') && !NUMERIC_INPUT_TYPES.includes(input.dataType) ? (
					<div className="multi-add-editor__input-default">
						<DefaultValueField
							compact
							element={{ name: input.key || `input-${n}`, dataType: input.dataType, label: { singular: input.label || '' }, options }}
							idPrefix={`multi-add-input-${input.uid}.options`}
							onPatch={partial => patchOptions(partial)}
						/>
					</div>
				) : null}
				{toggles.length ? (
					<div className="multi-add-editor__input-toggles">
						{toggles.map((row, i) => (
							<ToggleRow
								key={row.key}
								id={idFor(row.key)}
								label={row.label}
								description={toggleDescription(row, input.dataType)}
								checked={!!options[row.key]}
								onChange={checked => patchOptions(row.key === 'allowMultiple' && input.dataType === 'choice' && options.defaultValue !== undefined
									// "Allow multiple" changes the shape of a choice input's
									// default: a list of one when it turns on, the first entry
									// when it turns off (utils/choiceValues.js).
									? { allowMultiple: checked, defaultValue: defaultForMultiToggle(input, checked) }
									: { [row.key]: checked })}
								first={i === 0}
								size="small"
								aria-label={`Input ${n} ${row.label.toLowerCase()}`}
							/>
						))}
					</div>
				) : null}
			</>
		);
	};

	return (
		<div className={`multi-add-editor__input${dead ? ' multi-add-editor__input--dead' : ''}`} data-testid={testId}>
			<div className="multi-add-editor__input-head">
				<Input
					placeholder="Label"
					aria-label={`Input ${index + 1} label`}
					value={input.label}
					onChange={event => patch({ label: event.target.value })}
					status={dead ? 'error' : undefined}
				/>
				<DataTypePicker
					variant="select"
					ariaLabel={`Input ${index + 1} type`}
					exclude={excludedTypes}
					value={input.dataType}
					onChange={setType}
					className="multi-add-editor__input-type"
				/>
				<label className="multi-add-editor__inline">
					<Switch size="small" checked={!!input.options?.required} onChange={checked => patchOptions({ required: checked })} aria-label={`Input ${index + 1} required`} />
					<span>Required</span>
				</label>
				<Button type="text" size="small" icon={<TRASH />} aria-label={`Remove input ${index + 1}`} onClick={onRemove} />
			</div>
			{renderOptions()}
		</div>
	);
}
